import React, { useState, useEffect, useRef, useCallback } from 'react';
import { RiNotification3Line, RiNotification3Fill, RiCheckDoubleLine } from 'react-icons/ri';
import { getAnnouncements } from '../../api/quiz.api';
import { playMark } from '../brand/celebrate';

/* ══════════════════════════════════════════════════════════════
   ANNOUNCEMENT BELL

   The admin panel could publish announcements and the API served
   them, but nothing in the app had ever rendered one — the pipeline
   stopped one step short of a reader.

   A bell rather than a dismissible banner: a banner is read once and
   then gone forever, and it competes with the lesson for the top of
   the screen. A bell keeps every past announcement reachable, and
   costs nothing visually when there is nothing to say.

   "Read" is per-device in localStorage. Announcements are broadcast
   notices, not mail — a server-side per-user read receipt would be a
   table and an endpoint to maintain for something nobody audits.
══════════════════════════════════════════════════════════════ */

const SEEN_KEY = 'kitsu-seen-announcements';

const loadSeen = () => {
  try { return new Set(JSON.parse(localStorage.getItem(SEEN_KEY)) || []); }
  catch (_) { return new Set(); }
};

const saveSeen = set => {
  // Cap it so a long-lived install doesn't grow this forever.
  try { localStorage.setItem(SEEN_KEY, JSON.stringify([...set].slice(-200))); } catch (_) {}
};

function timeAgo(iso) {
  const secs = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (secs < 60) return 'just now';
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

export default function AnnouncementBell() {
  const [items, setItems] = useState([]);
  const [seen, setSeen] = useState(loadSeen);
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    let alive = true;
    getAnnouncements()
      .then(res => { if (alive) setItems(res.data || []); })
      .catch(() => {});   // a missing notice board must never break the app
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = e => { if (!boxRef.current?.contains(e.target)) setOpen(false); };
    const onKey = e => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const unread = items.filter(a => !seen.has(a._id));

  /* The bell already carries a dot, but the sidebar mark is the thing in
     peripheral vision. Beat once when unread mail appears, not on every
     render — hence the ref rather than firing straight from the count. */
  const announced = useRef(false);
  useEffect(() => {
    if (unread.length && !announced.current) {
      announced.current = true;
      playMark('pulse');
    }
    if (!unread.length) announced.current = false;
  }, [unread.length]);

  /* Opening the panel is the read receipt — you have seen them by
     definition, and a separate "mark read" click is busywork. */
  const toggle = useCallback(() => {
    /* Marking read is done here rather than inside the setOpen updater —
       a state updater must be pure, and React calls it twice in StrictMode. */
    if (!open && unread.length) {
      const next = new Set(seen);
      items.forEach(a => next.add(a._id));
      setSeen(next);
      saveSeen(next);
    }
    setOpen(o => !o);
  }, [open, unread.length, items, seen]);

  // Nothing published yet — no reason to show an empty bell at all.
  if (items.length === 0) return null;

  return (
    <div className="ann-bell" ref={boxRef}>
      <button
        type="button"
        className={`ann-bell-btn${unread.length ? ' unread' : ''}${open ? ' open' : ''}`}
        onClick={toggle}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={unread.length ? `${unread.length} unread announcements` : 'Announcements'}
        title={unread.length ? `${unread.length} new` : 'Announcements'}
      >
        {unread.length ? <RiNotification3Fill size={17} /> : <RiNotification3Line size={17} />}
        {unread.length > 0 && <span className="ann-bell-dot">{unread.length}</span>}
      </button>

      {open && (
        <div className="ann-panel anim-pop" role="dialog" aria-label="Announcements">
          <div className="ann-panel-head">
            <strong>Announcements</strong>
            {items.length > 0 && (
              <span><RiCheckDoubleLine size={13} /> all read</span>
            )}
          </div>

          <ul className="ann-list">
            {items.map(a => (
              <li key={a._id} className="ann-item">
                <div className="ann-item-head">
                  <strong>{a.title}</strong>
                  <time dateTime={a.createdAt}>{timeAgo(a.createdAt)}</time>
                </div>
                <p>{a.body}</p>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
